import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';
import actions from 'store/actions';
import selectors from 'store/selectors';
import ForecastReturnsTable from './components/ForecastReturnsTable';
import { isEmpty, isArray } from 'lodash';
import {
  ForecastReturnRawType,
  ForecastReturnType,
  RiskAndReturnType
} from 'utils/forecastReturnType';
import { timeHorizonEnum } from 'utils/timeHorizon';
import { RiskOrReturnEnum } from 'utils/riskOrReturn';
import { calcNearestStep } from './util';

const EvalueForecastReturns = ({
  currentRiskAndReturn,
  defaultRiskAndReturn,
  customRiskAndReturn,
  riskLevelDescriptions,
  setValues,
  saveTables,
  isSaving,
  isFirmAdmin,
  isCustom
}: any) => {
  const [mappedCurrentRiskAndReturn, setMappedCurrentRiskAndReturn] = useState(
    null as any
  );
  const [mappedDefaultRiskAndReturn, setMappedDefaultRiskAndReturn] = useState(
    null as any
  );
  const [mappedCustomRiskAndReturn, setMappedCustomRiskAndReturn] = useState(
    null as any
  );

  const updateValues = (
    time_horizon: timeHorizonEnum,
    risk_level: number,
    value: number,
    riskOrReturn: RiskOrReturnEnum
  ) => {
    const savedValues = mappedCustomRiskAndReturn as any;
    savedValues[time_horizon][risk_level][riskOrReturn] = calcNearestStep(
      value / 100,
      0.0001
    ).toFixed(4);
    setValues({ ...savedValues } as any);
  };

  const getReturnAndRiskData = (forecastReturns: ForecastReturnRawType[]) => {
    if (!isArray(forecastReturns)) {
      return forecastReturns;
    }

    return forecastReturns.reduce(
      (returnAndRisk: ForecastReturnType, fr: ForecastReturnRawType) => {
        const riskLevel = fr.risk_level;
        (returnAndRisk as any)[fr.time_horizon][riskLevel] = {
          return: fr.annual_return,
          risk: fr.risk
        } as RiskAndReturnType;
        return returnAndRisk;
      },
      {
        [timeHorizonEnum.THREE_TO_FIVE_YEARS]: {},
        [timeHorizonEnum.FIVE_TO_ELEVEN_YEARS]: {},
        [timeHorizonEnum.ELEVEN_TO_FIFTEEN_YEARS]: {},
        [timeHorizonEnum.SIXTEEN_TO_TWENTY_YEARS]: {},
        [timeHorizonEnum.TWENTYONE_PLUS_YEARS]: {}
      } as ForecastReturnType
    );
  };

  useEffect(() => {
    if (!isEmpty(currentRiskAndReturn)) {
      setMappedCurrentRiskAndReturn(getReturnAndRiskData(currentRiskAndReturn));
    }
    if (!isEmpty(defaultRiskAndReturn)) {
      setMappedDefaultRiskAndReturn(getReturnAndRiskData(defaultRiskAndReturn));
    }
    if (!isEmpty(customRiskAndReturn)) {
      setMappedCustomRiskAndReturn(getReturnAndRiskData(customRiskAndReturn));
    }
  }, [currentRiskAndReturn, defaultRiskAndReturn, customRiskAndReturn]);

  return (
    <>
      {!isEmpty(mappedCurrentRiskAndReturn) && (
        <>
          <ForecastReturnsTable
            riskLevelDescriptions={riskLevelDescriptions}
            timeHorizon="3-5 years"
            returnAndRisk={
              mappedCurrentRiskAndReturn[timeHorizonEnum.THREE_TO_FIVE_YEARS]
            }
            defaultRiskAndReturn={
              mappedDefaultRiskAndReturn[timeHorizonEnum.THREE_TO_FIVE_YEARS]
            }
            updateValues={updateValues}
            timeHorizonEnumVal={timeHorizonEnum.THREE_TO_FIVE_YEARS}
            saveTables={saveTables}
            isSaving={isSaving}
            isFirmAdmin={isFirmAdmin}
            isCustom={isCustom}
          />
          <ForecastReturnsTable
            riskLevelDescriptions={riskLevelDescriptions}
            timeHorizon="6-10 years"
            returnAndRisk={
              mappedCurrentRiskAndReturn[timeHorizonEnum.FIVE_TO_ELEVEN_YEARS]
            }
            defaultRiskAndReturn={
              mappedDefaultRiskAndReturn[timeHorizonEnum.FIVE_TO_ELEVEN_YEARS]
            }
            updateValues={updateValues}
            timeHorizonEnumVal={timeHorizonEnum.FIVE_TO_ELEVEN_YEARS}
            saveTables={saveTables}
            isSaving={isSaving}
            isFirmAdmin={isFirmAdmin}
            isCustom={isCustom}
          />
          <ForecastReturnsTable
            riskLevelDescriptions={riskLevelDescriptions}
            timeHorizon="11-15 years"
            returnAndRisk={
              mappedCurrentRiskAndReturn[
                timeHorizonEnum.ELEVEN_TO_FIFTEEN_YEARS
              ]
            }
            defaultRiskAndReturn={
              mappedDefaultRiskAndReturn[
                timeHorizonEnum.ELEVEN_TO_FIFTEEN_YEARS
              ]
            }
            updateValues={updateValues}
            timeHorizonEnumVal={timeHorizonEnum.ELEVEN_TO_FIFTEEN_YEARS}
            saveTables={saveTables}
            isSaving={isSaving}
            isFirmAdmin={isFirmAdmin}
            isCustom={isCustom}
          />
          <ForecastReturnsTable
            riskLevelDescriptions={riskLevelDescriptions}
            timeHorizon="16-20 years"
            returnAndRisk={
              mappedCurrentRiskAndReturn[
                timeHorizonEnum.SIXTEEN_TO_TWENTY_YEARS
              ]
            }
            defaultRiskAndReturn={
              mappedDefaultRiskAndReturn[
                timeHorizonEnum.SIXTEEN_TO_TWENTY_YEARS
              ]
            }
            updateValues={updateValues}
            timeHorizonEnumVal={timeHorizonEnum.SIXTEEN_TO_TWENTY_YEARS}
            saveTables={saveTables}
            isSaving={isSaving}
            isFirmAdmin={isFirmAdmin}
            isCustom={isCustom}
          />
          <ForecastReturnsTable
            riskLevelDescriptions={riskLevelDescriptions}
            timeHorizon="21+ years"
            returnAndRisk={
              mappedCurrentRiskAndReturn[timeHorizonEnum.TWENTYONE_PLUS_YEARS]
            }
            defaultRiskAndReturn={
              mappedDefaultRiskAndReturn[timeHorizonEnum.TWENTYONE_PLUS_YEARS]
            }
            updateValues={updateValues}
            timeHorizonEnumVal={timeHorizonEnum.TWENTYONE_PLUS_YEARS}
            saveTables={saveTables}
            isSaving={isSaving}
            isFirmAdmin={isFirmAdmin}
            isCustom={isCustom}
          />
        </>
      )}
    </>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskProfiler: selectors.firmSelectors.riskProfiler(state)
    }),
    {
      updateMetaReturnForecasts: actions.metaActions.updateMetaReturnForecasts
    }
  )
)(EvalueForecastReturns);
